import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Target, CheckCircle2, AlertCircle } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";
import PasswordInput from "../components/PasswordInput";

export default function Login() {
  const navigate = useNavigate();
  const { login, refresh } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useDocumentTitle("Login");

  const verify = params.get("verify");

  useEffect(() => {
    if (verify === "success") {
      toast.success("Email verified! You can now sign in.");
      // Refresh /me in case the user is already logged in
      refresh();
    } else if (verify === "expired") {
      toast.error("Verification link expired. Sign in and request a new one.");
    } else if (verify === "invalid") {
      toast.error("Verification link is invalid.");
    }
  }, [verify, refresh]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      const returnTo = params.get("return_to");
      navigate(returnTo && returnTo.startsWith("/") ? returnTo : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-zinc-950 text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,47,167,0.4),transparent_60%)]" />
        <Link to="/" className="relative flex items-center gap-2">
          <span className="h-8 w-8 rounded-md bg-[#002FA7] grid place-items-center">
            <Target className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <span className="font-display text-lg font-bold">QaTestology</span>
        </Link>
        <div className="relative">
          <div className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-3">// welcome back</div>
          <h2 className="font-display text-4xl font-black tracking-tighter leading-tight">
            Your next QA role<br />is one login away.
          </h2>
          <p className="mt-4 text-zinc-400 max-w-md">
            Pick up where you left off — applications, saved jobs, and your AI skill gap analysis.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <span className="h-8 w-8 rounded-md bg-[#002FA7] text-white grid place-items-center">
              <Target className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="font-display text-lg font-bold">QaTestology</span>
          </Link>

          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950">Sign in</h1>
          <p className="mt-2 text-sm text-zinc-500">Welcome back to QaTestology.</p>

          {verify === "success" && (
            <div className="mt-5 p-3 rounded-md bg-emerald-50 border border-emerald-200 flex items-start gap-2" data-testid="verify-success-banner">
              <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-emerald-800">Your email is verified — you can sign in now.</p>
            </div>
          )}
          {verify === "expired" && (
            <div className="mt-5 p-3 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="verify-expired-banner">
              <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">Your verification link has expired. Sign in and click "Resend email" from your dashboard.</p>
            </div>
          )}

          <div className="mt-5 p-4 rounded-md bg-blue-50 border border-blue-200" data-testid="demo-credentials-card">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] tag-mono uppercase tracking-widest text-[#002FA7] font-bold">demo accounts</span>
              <span className="text-[10px] tag-mono text-zinc-500">click to prefill</span>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { role: "Job Seeker", email: "demo.seeker@qatestology.com", pw: "Demo@1234", testid: "demo-fill-seeker" },
                { role: "Employer",   email: "demo.employer@qatestology.com", pw: "Demo@1234", testid: "demo-fill-employer" },
              ].map((d) => (
                <button
                  key={d.role}
                  type="button"
                  onClick={() => { setEmail(d.email); setPassword(d.pw); }}
                  data-testid={d.testid}
                  className="text-left rounded border border-blue-200 bg-white hover:border-[#002FA7] hover:shadow-sm transition-all px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-zinc-950">{d.role}</span>
                    <span className="tag-mono text-[10px] text-[#002FA7]">use →</span>
                  </div>
                  <div className="tag-mono text-[11px] text-zinc-600 truncate">{d.email}</div>
                  <div className="tag-mono text-[11px] text-zinc-500">password: {d.pw}</div>
                </button>
              ))}
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="mt-6 w-full h-11 border-zinc-300"
            onClick={loginWithGoogle}
            data-testid="login-google-btn"
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-zinc-200" />
            <span className="text-xs uppercase tracking-widest text-zinc-400 font-mono">or</span>
            <div className="h-px flex-1 bg-zinc-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs font-semibold text-[#002FA7] hover:text-[#00227A] tag-mono"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                showTestId="login-password-toggle"
                className="mt-1.5"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-[#002FA7] hover:bg-[#00227A] text-white"
            >
              {submitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-zinc-600">
            New here?{" "}
            <Link to="/signup" className="font-semibold text-[#002FA7] hover:text-[#00227A]" data-testid="link-to-signup">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
